import { useState, useEffect } from "react";
import { db } from "../../firebase";
import {
  collection, doc, updateDoc,
  query, orderBy, onSnapshot,
} from "firebase/firestore";
import { IC } from "../shared";

// ── Notification Icons (SVG, color-coded) ────────────────────────────────────
const NOTIF_ICONS = {
  request:           { icon: IC.seat,   color: "#2b5be0", bg: "rgba(43,91,224,0.1)" },
  accepted:          { icon: IC.check,  color: "#0f9e68", bg: "rgba(15,158,104,0.1)" },
  rejected:          { icon: IC.x,      color: "#c0392b", bg: "rgba(192,57,43,0.1)" },
  cancelled:         { icon: IC.x,      color: "#8a90a0", bg: "rgba(138,144,160,0.1)" },
  request_withdrawn: { icon: IC.x,      color: "#8a90a0", bg: "rgba(138,144,160,0.1)" },
  message:           { icon: IC.chat,   color: "#5a80f0", bg: "rgba(90,128,240,0.1)" },
  route_update:      { icon: IC.pin,    color: "#2b5be0", bg: "rgba(43,91,224,0.1)" },
};
const DEFAULT_ICON = { icon: IC.bell, color: "#2b5be0", bg: "rgba(43,91,224,0.1)" };

const formatTime = (ts) => {
  if (!ts?.toDate) return "Just now";
  const d = ts.toDate();
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1)  return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24)   return `${diffH}h ago`;
  return d.toLocaleDateString("en", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

// ── Notifications Screen ─────────────────────────────────────────────────────
const Alerts = ({ currentUser, onCount, onChatCount, onNavigate }) => {
  const [notifs, setNotifs]   = useState([]);

  useEffect(() => {
    const q = query(collection(db, "notifications", currentUser.id, "items"), orderBy("ts", "desc"));
    const unsub = onSnapshot(q, snap => {
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(n => !n.dismissed);
      setNotifs(items);
      const unread = items.filter(n => !n.read);
      onCount(unread.filter(n => n.type !== "message").length);
      onChatCount?.(unread.filter(n => n.type === "message").length);
    });
    return unsub;
  }, [currentUser.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = async (n) => {
    if (!n.read) {
      await updateDoc(doc(db, "notifications", currentUser.id, "items", n.id), { read: true });
    }
    if (n.tab && onNavigate) onNavigate(n.tab, n.subview);
  };

  const clearAll = async () => {
    // Mark all as read + dismissed — they stay in Firestore but won't show in UI
    await Promise.all(notifs.map(n => updateDoc(doc(db, "notifications", currentUser.id, "items", n.id), { read: true, dismissed: true })));
  };

  return (
    <div className="screen" style={{ padding: "20px 0 90px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", marginBottom: 14 }}>
        <h2 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 22, color: "var(--ink)" }}>Notifications</h2>
        {notifs.length > 0 && (
          <button onClick={clearAll} style={{ fontSize: 11, fontFamily: "Syne", fontWeight: 700, color: "var(--accent)", background: "rgba(43,91,224,.08)", border: "none", borderRadius: 10, padding: "6px 12px", cursor: "pointer", transition: "all .15s" }}>
            Clear all
          </button>
        )}
      </div>

      {/* Empty state */}
      {notifs.length === 0 ? (
        <div className="igo-empty" style={{ padding: "50px 20px" }}>
          <div className="igo-empty-illust">
            <span style={{ width: 48, height: 48, color: "var(--accent)", opacity: .6 }}>{IC.bell}</span>
          </div>
          <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 17, color: "var(--ink)", marginBottom: 6 }}>No notifications yet</div>
          <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.5 }}>Ride updates and messages will appear here</div>
        </div>
      ) : (
        notifs.map((n, i) => {
          const iconDef = NOTIF_ICONS[n.type] || DEFAULT_ICON;
          return (
            <div key={n.id} onClick={() => handleClick(n)} className="anotif" style={{
              animationDelay: `${i * 40}ms`,
              display: "flex", gap: 12, padding: "14px 20px",
              borderBottom: "1px solid var(--border)",
              background: n.read ? "transparent" : "rgba(43,91,224,.03)",
              cursor: n.tab ? "pointer" : "default",
              transition: "background .15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = n.read ? "var(--paper)" : "rgba(43,91,224,.05)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = n.read ? "transparent" : "rgba(43,91,224,.03)"; }}
            >
              {/* Icon */}
              <div style={{ width: 38, height: 38, borderRadius: 12, background: iconDef.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ width: 16, height: 16, color: iconDef.color }}>{iconDef.icon}</span>
              </div>

              {/* Body */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: "var(--ink)", lineHeight: 1.4 }}>{(() => { const m = n.text.match(/^([A-Z][a-z]+ [A-Z][a-z]+)(.*)/); return m ? <><strong>{m[1]}</strong>{m[2]}</> : n.text; })()}</div>
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{formatTime(n.ts)}</div>
              </div>

              {/* Unread dot */}
              {!n.read && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--accent)", flexShrink: 0, marginTop: 5 }}/>}
            </div>
          );
        })
      )}
    </div>
  );
};

export default Alerts;
